import { useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import { Link } from 'react-router-dom';

import { AUTH_MODE, unwrap } from '../api/client';
import { forgotPassword, register, resendVerification } from '../api/gen';
import { oidc, oidcConfigured } from '../lib/oidc';
import { useSession } from '../lib/session';

// The one screen an unpaired device has.
//
// A parent signs in once and the tablet learns the family: its id, and the
// names of the children in it. After that this page is not seen again in
// ordinary use — a child never signs in as a parent, and the profile picker is
// what the app boots to.
//
// Under authit — the default — the credential is an address and a password
// this server keeps, so registering, confirming and resetting all happen here.
// Under Zitadel there is nothing to fill in and the hosted login owns it.
// Under the header seam — local development and the end-to-end suite — the
// subject is typed, which is exactly as unsafe as it sounds and is why it is a
// build-time setting the production bundle cannot be talked into.

type Panel = 'signIn' | 'register' | 'forgot';

export function SignInPage() {
  return (
    <Center mih="100vh" p="md">
      <Card withBorder padding="xl" radius="md" w={420} maw="100%">
        <Stack>
          <Title order={3}>Sokrates</Title>
          <Text size="sm" c="dimmed">
            Dieses Gerät ist noch mit keiner Familie verknüpft. Melde dich einmal an — danach
            genügt den Kindern ihre PIN.
          </Text>
          {AUTH_MODE === 'authit' ? (
            <PasswordPanels />
          ) : AUTH_MODE === 'header' ? (
            <HeaderSeam />
          ) : (
            <Zitadel />
          )}
          <Anchor component={Link} to="/datenschutz" size="xs" c="dimmed">
            Datenschutz
          </Anchor>
        </Stack>
      </Card>
    </Center>
  );
}

function PasswordPanels() {
  const { signInPassword } = useSession();
  const [panel, setPanel] = useState<Panel>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // One runner for all three panels: they differ in the call and in what they
  // say afterwards, not in how a failure is shown.
  const run = (what: () => Promise<string>) => {
    setBusy(true);
    setError('');
    setNotice('');
    void (async () => {
      try {
        setNotice(await what());
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(false);
      }
    })();
  };

  const messages = (
    <>
      {error ? (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert color="green" variant="light">
          {notice}
        </Alert>
      ) : null}
    </>
  );

  if (panel === 'register') {
    return (
      <Stack>
        {messages}
        <TextInput
          label="E-Mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <PasswordInput
          label="Passwort"
          description="Mindestens zwölf Zeichen."
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <Button
          disabled={!email || !password || busy}
          loading={busy}
          onClick={() =>
            run(async () => {
              await unwrap(register({ body: { email, password } }));
              setPassword('');
              return 'Wir haben eine E-Mail geschickt. Bestätige die Adresse über den Link darin, dann kannst du dich anmelden.';
            })
          }
        >
          Konto anlegen
        </Button>
        <Group justify="space-between">
          <Anchor size="sm" onClick={() => setPanel('signIn')}>
            Ich habe schon ein Konto
          </Anchor>
          <Anchor
            size="sm"
            onClick={() =>
              run(async () => {
                await unwrap(resendVerification({ body: { email } }));
                return 'Falls es zu dieser Adresse ein unbestätigtes Konto gibt, ist die E-Mail unterwegs.';
              })
            }
          >
            E-Mail erneut senden
          </Anchor>
        </Group>
      </Stack>
    );
  }

  if (panel === 'forgot') {
    return (
      <Stack>
        {messages}
        <TextInput
          label="E-Mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Button
          disabled={!email || busy}
          loading={busy}
          onClick={() =>
            run(async () => {
              await unwrap(forgotPassword({ body: { email } }));
              return 'Falls es zu dieser Adresse ein Konto gibt, liegt der Link zum neuen Passwort im Postfach.';
            })
          }
        >
          Link anfordern
        </Button>
        <Anchor size="sm" onClick={() => setPanel('signIn')}>
          Zurück zur Anmeldung
        </Anchor>
      </Stack>
    );
  }

  return (
    <Stack>
      {messages}
      <TextInput
        label="E-Mail"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <PasswordInput
        label="Passwort"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
      />
      <Button
        disabled={!email || !password || busy}
        loading={busy}
        onClick={() =>
          run(async () => {
            await signInPassword(email, password);
            return '';
          })
        }
      >
        Anmelden
      </Button>
      <Group justify="space-between">
        <Anchor size="sm" onClick={() => setPanel('register')}>
          Konto anlegen
        </Anchor>
        <Anchor size="sm" onClick={() => setPanel('forgot')}>
          Passwort vergessen
        </Anchor>
      </Group>
    </Stack>
  );
}

function HeaderSeam() {
  const { signInParent } = useSession();
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const go = async () => {
    setBusy(true);
    setError('');
    try {
      await signInParent(subject, subject);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      {error ? (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      ) : null}
      <TextInput
        label="Subject"
        description="Dieser Build nutzt den Header-Seam der Tests."
        placeholder="user-weber-parent"
        value={subject}
        onChange={(e) => setSubject(e.currentTarget.value)}
      />
      <Button disabled={!subject || busy} loading={busy} onClick={() => void go()}>
        Weiter
      </Button>
    </Stack>
  );
}

function Zitadel() {
  const [busy, setBusy] = useState(false);
  if (!oidcConfigured()) {
    return (
      <Alert>
        Diesem Build wurde kein Zitadel mitgegeben (VITE_ZITADEL_ISSUER).
      </Alert>
    );
  }
  return (
    <Button
      loading={busy}
      onClick={() => {
        setBusy(true);
        void oidc().signinRedirect();
      }}
    >
      Als Elternteil anmelden
    </Button>
  );
}
